import { toast } from 'sonner';
import i18n from 'i18n';
import { gradeItem } from 'services/api/product';
import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { useProductStore } from '../store/productStore';
import { useReceiptStore } from '../store/receiptStore';
import { GradeData, ProductLine, SubmitApiResult } from 'types';
import { SCREEN_TYPES } from 'utils/constants';
import { playErrorSound, playGoodSound } from 'utils/sounds';
import { closeCurrentReceipt, isReceiptReadyToAutoClose } from 'utils/closeReceiptAction';

const onGradeComplete = (updatedProduct: ProductLine): void => {
  const { setCurrentProduct } = useProductStore.getState();
  const { receiptData, setReceiptData } = useReceiptStore.getState();
  setCurrentProduct(updatedProduct);
  // Update receiptData with the graded line
  if (receiptData) {
    const updatedLines =
      receiptData.lines?.map((line) =>
        line?.receipt_line === updatedProduct.receipt_line
          ? { ...line, ...updatedProduct, receipt_ind: 'y' }
          : line
      ) || [];
    setReceiptData({ ...receiptData, lines: updatedLines });
  }
};

export const submitCurrentGradeItem = async (isRejected?: boolean): Promise<SubmitApiResult> => {
  const { user, stationId } = useAuthStore.getState();
  const {
    currentProduct: productLine,
    isProductNew,
    productCondition,
    setShowGradingSheet,
  } = useProductStore.getState();
  const { setCurrentScreen } = useAppStore.getState();

  try {
    const gradeData: GradeData = {
      user_id: user.id,
      station_id: stationId,
      transaction_id: productLine.transaction_id,
      receipt_line: productLine.receipt_line,
      is_new: isProductNew,
      product_condition: productCondition,
      upc: productLine.upc,
      vendor_id: productLine.vendor_id,
      exception_process: productLine.exception_process,
      disposition_stage: productLine.disposition_stage,
    };

    const result = await gradeItem(gradeData, isRejected);

    if (result?.responseState !== 'confirmed') {
      playErrorSound();
      toast.error(result?.responseMsg || i18n.t('toast.failedGradeItem'));
      return { success: false };
    }

    toast.success(i18n.t('toast.itemGradedSuccess'));
    // Update product with disposition_stage from response
    onGradeComplete({
      ...productLine,
      receipt_grade: result.receipt_grade,
      disposition_stage: result.disposition_stage,
      exception_process: result.exception_process,
      receipt_ind: 'y',
    } as ProductLine);

    // Users often forget to close the receipt after the last item, so close it for them.
    if (isReceiptReadyToAutoClose(useReceiptStore.getState().receiptData)) {
      const closed = await closeCurrentReceipt({ auto: true });
      if (closed) return { success: true };
    }

    setShowGradingSheet(true);
    setCurrentScreen(SCREEN_TYPES.SCAN_BOX);
    playGoodSound();

    return { success: true };
  } catch {
    playErrorSound();
    toast.error(i18n.t('toast.networkError'));
    return { success: false };
  }
};
